import React from "react";
import gql from "graphql-tag";
import { Link, navigate, RouteComponentProps } from "@reach/router";
import times from "lodash/times";
import { Breakpoint } from "react-socks";
import { BrandCircle } from "@organisms/brandCircleComponents";
import { codePointAt } from "@mocks/fileMock";
import { FavoriteBrands_Viewer_favoriteBrands as TBrand } from "@queries";
import { Box, Flex } from "@atoms";
import { ContentBlock, CircleAddIcon, CircleText } from "@molecules";
import { constants, RailsUrl, spelling } from "@services";
import { useStoreAppValue } from "@hooks";

// == Types ================================================================

interface IProps {
  brands: TBrand[];
}

// == Constants ============================================================

const FRAGMENTS = {
  fields: gql`
    fragment BrandFavoritesDialerFragment on Brand {
      id
      name
      logoImage
      logoBackgroundColor
      invertedLogoImage
      viewerHasFavorited
    }
  `,
};

export enum BREAKPOINTS_ENUM {
  XSMALL = "XSMALL",
  SMALL = "SMALL",
  MEDIUM = "MEDIUM",
  LARGE = "LARGE",
}

const maxCircles = {
  XSMALL: 4,
  SMALL: 5,
  MEDIUM: 7,
  LARGE: 9,
};

// == Component ============================================================

const BrandFavoritesDialer = ({ brands }: IProps) => {
  const brandsCount = brands.length;

  const { currency } = useStoreAppValue();

  const onEditClick = () => {
    navigate(RailsUrl.favoritesUrl());
  };

  const renderBrandCircle = (brand) => {
    return (
      <Link to={RailsUrl.brandModalUrl({ id: brand.id })}>
        <BrandCircle brand={brand} />
      </Link>
    );
  };

  const circleRenderSelector = (index: number, maxCirclesPerRow: number) => {
    const brandOverflowCount = brandsCount - (maxCirclesPerRow - 1);
    if (!brands[index]) {
      return (
        <Box
          key={`brand-fab-empty-circle-${index}`}
          mt={index < maxCirclesPerRow ? 0 : 2}
          px={[1, 2]}
          width={1 / maxCirclesPerRow}
        >
          <Link to={`?modal=${constants.favoritesParam}`}>
            <CircleAddIcon />
          </Link>
        </Box>
      );
    }

    // Last item, and greater than brands to show
    if (index === maxCirclesPerRow - 1 && brandsCount >= maxCirclesPerRow) {
      return (
        <Box
          key={`brand-fab-empty-circle-${index}`}
          mt={index < maxCirclesPerRow ? 0 : 2}
          px={[1, 2]}
          width={1 / maxCirclesPerRow}
        >
          <Link to={`?modal=${constants.favoritesParam}`}>
            <CircleText text={`+${brandOverflowCount}`} />
          </Link>
        </Box>
      );
    }

    // Default brand item
    return (
      <Box
        key={brands[index].id}
        mt={index < maxCirclesPerRow ? 0 : 2}
        px={[1, 2]}
        textAlign="center"
        width={1 / maxCirclesPerRow}
      >
        {renderBrandCircle(brands[index])}
      </Box>
    );
  };

  const renderRow = (breakpointEnum: BREAKPOINTS_ENUM) => {
    return (
      <Flex flexWrap="wrap" mx={-1}>
        {times(maxCircles[breakpointEnum], (index) =>
          circleRenderSelector(index, maxCircles[breakpointEnum])
        )}
      </Flex>
    );
  };

  return (
    <ContentBlock
      altLink="Edit"
      altOnClick={onEditClick}
      title={spelling("favorites", {
        country: `${currency === "USD" ? "USA" : "Canada"}`,
        capitalize: true,
        singular: false,
      })}
    >
      <Breakpoint xsmall>{renderRow(BREAKPOINTS_ENUM.XSMALL)}</Breakpoint>
      <Breakpoint small>{renderRow(BREAKPOINTS_ENUM.SMALL)}</Breakpoint>
      <Breakpoint medium>{renderRow(BREAKPOINTS_ENUM.MEDIUM)}</Breakpoint>
      <Breakpoint large up>
        {renderRow(BREAKPOINTS_ENUM.LARGE)}
      </Breakpoint>
    </ContentBlock>
  );
};

BrandFavoritesDialer.fragments = FRAGMENTS;

export default BrandFavoritesDialer;

// == Styles ===============================================================
